import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useNavigate } from "@tanstack/react-router";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { BOARD_MAX_BODY, type BoardPin, type BoardPost, type BoardReply, type Club } from "@pagemates/shared";
import { api, describeError } from "../lib/api";
import { posLabel } from "../lib/format";
import { Avatar, BackBar, Spinner, useScrollLock, useSheetDrag } from "./ui";
import { ProfileFace } from "./Profile";
import { renderBody, when } from "./Discussion";
import { CloseIcon, LockIcon, MoreIcon, MoveIcon, SendIcon } from "./Icons";
import { club } from "@pagemates/shared/copy";

const txt = club.noticeboard;

/*
 * The noticeboard (PLAN.md §Noticeboard): a wall for club talk that isn't
 * about the book. One pinned notice on paper, a composer, the spoiler line,
 * then posts by latest activity with one level of replies. Threads open
 * inline on desktop and as their own screen on phones (?post=<id>).
 */

const WIDE = "(min-width: 900px)";
export function useWide(): boolean {
  const [wide, setWide] = useState(() => typeof window !== "undefined" && window.matchMedia(WIDE).matches);
  useEffect(() => {
    const mq = window.matchMedia(WIDE);
    const on = () => setWide(mq.matches);
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, []);
  return wide;
}

const dayShort = (iso: string) => new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
const excerpt = (s: string, n = 40) => { const l = s.split("\n").find((x) => x.trim())?.trim() ?? ""; return l.length > n ? `${l.slice(0, n - 1).trimEnd()}…` : l; };
const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

type Me = { id: string; name: string; image: string | null };
interface Ctx { clubId: string; club: Club; me: Me; isAdmin: boolean; book: { id: string; title: string } | null; pinned: BoardPin | null; refresh: () => Promise<void> }

function useRefresh(clubId: string) {
  const qc = useQueryClient();
  return async () => {
    await Promise.all([
      qc.invalidateQueries({ queryKey: ["board", clubId] }),
      qc.invalidateQueries({ queryKey: ["board-thread", clubId] }),
      qc.invalidateQueries({ queryKey: ["club", clubId] }),
    ]);
  };
}

/** Tell the server the board was opened, once after a moment and again on leaving; the dot and the "N new" card go. */
function useMarkSeen(clubId: string) {
  const qc = useQueryClient();
  useEffect(() => {
    const seen = () => api.boardSeen(clubId).then(() => qc.invalidateQueries({ queryKey: ["club", clubId] })).catch(() => undefined);
    const t = setTimeout(() => void seen(), 1500);
    return () => { clearTimeout(t); void seen(); };
  }, [clubId, qc]);
}

export function Noticeboard({ club, me, openPost, setOpenPost }: { club: Club; me: Me; openPost: string | null; setOpenPost: (id: string | null) => void }) {
  const clubId = club.id;
  const wide = useWide();
  const refresh = useRefresh(clubId);
  useMarkSeen(clubId);
  const q = useInfiniteQuery({
    queryKey: ["board", clubId],
    queryFn: ({ pageParam }) => api.board(clubId, pageParam),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.next,
    refetchInterval: 30_000, refetchOnWindowFocus: true,
  });
  // "New" markers stay put for the whole visit, even after the server hears we looked.
  const newIds = useRef(new Set<string>());
  useEffect(() => {
    for (const p of q.data?.pages ?? []) {
      for (const x of [...p.posts, ...(p.pinned ? [p.pinned.post] : [])]) { if (x.isNew) newIds.current.add(x.id); if (x.latestReply?.isNew) newIds.current.add(x.latestReply.id); }
    }
  }, [q.data]);

  if (q.isPending) return <section className="bd"><Spinner /></section>;
  if (q.isError) return <section className="bd"><p className="error">{describeError(q.error)}</p></section>;
  const first = q.data.pages[0]!;
  const posts = q.data.pages.flatMap((p) => p.posts);
  const ctx: Ctx = { clubId, club, me, isAdmin: club.myRole !== "member", book: first.currentBook, pinned: first.pinned, refresh };
  const fresh = new Set<string>();
  for (const x of [...posts, ...(first.pinned ? [first.pinned.post] : [])]) { if (x.isNew) fresh.add(x.id); if (x.latestReply?.isNew) fresh.add(x.latestReply.id); }
  const isNew = (id: string) => fresh.has(id) || newIds.current.has(id);
  const toggle = (id: string) => setOpenPost(openPost === id ? null : id);
  const inline = (id: string) => wide && openPost === id ? <InlineThread ctx={ctx} postId={id} /> : null;

  return (
    <section className="bd" aria-labelledby="bd-title">
      <div className="bd-h"><h2 id="bd-title">{txt.noticeboard}</h2><span className="muted small desktop-only">{txt.anythingThatIsnT}</span></div>
      {first.pinned && (
        <PinnedNotice ctx={ctx} pin={first.pinned} isNew={isNew(first.pinned.post.id)} onOpen={() => toggle(first.pinned!.post.id)}>{inline(first.pinned.post.id)}</PinnedNotice>
      )}
      <BoardComposer ctx={ctx} />
      <p className="bd-hint muted small">
        <LockIcon width={13} height={13} />
        {ctx.book
          ? <span>{txt.noSpoilersHereTalk(ctx.book.title)}<Link to="/clubs/$clubId/talk" params={{ clubId: club.slug }}>{txt.talk}</Link>{txt.whereItStaysHidden}</span>
          : <span>{txt.bookTalkGoesIn}<Link to="/clubs/$clubId/talk" params={{ clubId: club.slug }}>{txt.talk}</Link>.</span>}
      </p>
      {posts.length === 0 && !first.pinned && !first.next ? (
        <div className="empty-state bd-empty">
          <h3>{txt.nothingOnTheNoticeboard}</h3>
          <p>{txt.welcomePeopleSortOut}</p>
        </div>
      ) : posts.length > 0 && (
        <div className="bd-posts">
          {posts.map((p) => p.moved ? <MovedLine key={p.id} p={p} meId={me.id} /> : (
            <PostRow key={p.id} ctx={ctx} p={p} isNew={isNew(p.id)} newReply={p.latestReply ? isNew(p.latestReply.id) : false} open={openPost === p.id} onOpen={() => toggle(p.id)}>{inline(p.id)}</PostRow>
          ))}
        </div>
      )}
      {q.hasNextPage && (
        <button type="button" className="linkish small bd-older" disabled={q.isFetchingNextPage} onClick={() => void q.fetchNextPage()}>{q.isFetchingNextPage ? txt.loading : txt.olderPosts}</button>
      )}
    </section>
  );
}

/** The pinned notice: printed on paper with a brass pin, slightly rotated, above the composer. */
function PinnedNotice({ ctx, pin, isNew, onOpen, children }: { ctx: Ctx; pin: BoardPin; isNew: boolean; onOpen: () => void; children?: React.ReactNode }) {
  const p = pin.post;
  const names = useMemo(() => [p.author.name, ...(p.latestReply ? [p.latestReply.author.name] : [])], [p]);
  return (
    <div className="bd-notice-wrap">
      <article className="bd-notice" aria-label={txt.pinnedNotice}>
        <div className="bd-notice-who">
          <span>{txt.pinnedBy(pin.by ? (pin.by.id === ctx.me.id ? txt.you : pin.by.name) : txt.anAdmin, dayShort(pin.at), pin.until ? txt.until(dayShort(pin.until)) : "")}</span>
          <PostMenu ctx={ctx} p={p} onReply={onOpen} paper />
        </div>
        {p.author.id !== pin.by?.id && <div className="bd-notice-by">{p.mine ? txt.you2 : p.author.name}{isNew ? txt.new : ""}</div>}
        <PostBody ctx={ctx} p={p} names={names} className="bd-notice-text" />
        <button type="button" className="bd-notice-replies" onClick={onOpen}>{p.replyCount ? plural(p.replyCount, txt.reply, txt.replies) : txt.reply2}</button>
      </article>
      {children}
    </div>
  );
}

function PostBody({ ctx, p, names, className }: { ctx: Ctx; p: BoardReply; names: string[]; className: string }) {
  const [editing, setEditing] = useState(false);
  useEffect(() => { const on = (e: Event) => { if ((e as CustomEvent).detail === p.id) setEditing(true); }; window.addEventListener("bd-edit", on); return () => window.removeEventListener("bd-edit", on); }, [p.id]);
  if (editing && p.body !== null) return <Editor ctx={ctx} p={p} onDone={() => setEditing(false)} />;
  return <p className={className}>{p.deleted ? <span className="muted">{txt.thisPostWasDeleted}</span> : renderBody(p.body ?? "", names)}{p.editedAt && !p.deleted ? <span className="bd-edited">{txt.edited}</span> : null}</p>;
}

function PostRow({ ctx, p, isNew, newReply, open, onOpen, children }: { ctx: Ctx; p: BoardPost; isNew: boolean; newReply: boolean; open: boolean; onOpen: () => void; children?: React.ReactNode }) {
  const names = [p.author.name, ...(p.latestReply ? [p.latestReply.author.name] : [])];
  const r = p.latestReply;
  return (
    <article className={`bd-post${open ? " open" : ""}`}>
      {p.deleted ? <Avatar name={p.author.name} image={p.author.image} /> : <ProfileFace user={p.author} />}
      <div className="bd-body">
        <div className="bd-meta">
          {isNew && <span className="newdot" aria-label={txt.new2} />}
          <b>{p.deleted ? "" : p.mine ? txt.you2 : p.author.name}</b>
          {p.author.former && !p.deleted && <span className="pill">{txt.left}</span>}
          <span className="muted">{when(p.createdAt)}</span>
          {!p.deleted && <PostMenu ctx={ctx} p={p} onReply={onOpen} />}
        </div>
        <PostBody ctx={ctx} p={p} names={names} className="bd-text" />
        {r && !open && (
          <button type="button" className="bd-peek" onClick={onOpen}>
            <Avatar name={r.author.name} image={r.author.image} />
            <span className="bd-peek-t"><span className="small"><b>{r.mine ? txt.you2 : r.author.name}</b> <span className="muted">{when(r.createdAt)}</span>{newReply && <span className="newdot" aria-label={txt.newReply} />}</span>
              <span className="bd-peek-body">{r.body}</span></span>
          </button>
        )}
        <div className="bd-acts">
          {p.replyCount > 0 && <button type="button" className="linkish" aria-expanded={open} onClick={onOpen}>{open ? txt.hideReplies : plural(p.replyCount, txt.reply, txt.replies)}</button>}
          {!open && <button type="button" className="bd-reply" onClick={onOpen}>{txt.reply2}</button>}
        </div>
        {children}
      </div>
    </article>
  );
}

/** Where a post was before it moved into the book's discussion. The chip is the author's spot, so it's neutral. */
/**
 * Where a post was before it moved into the book's discussion. The chip is where it landed, not the viewer's
 * spot, so it's neutral. When someone other than the author moved it, the line names them: for now that's
 * how the author finds out.
 */
function MovedLine({ p, meId }: { p: BoardPost; meId: string }) {
  const m = p.moved!;
  const whose = p.mine ? txt.your : txt.s(p.author.name);
  const chip = <span className="pos">{landing(m.position, m.pages)}</span>;
  return (
    <div className="bd-moved">
      <MoveIcon width={15} height={15} />
      <span>{m.by
        ? <>{txt.movedPostTo(m.by.id === meId ? txt.you2 : m.by.name, whose, m.book.title)}{chip}</>
        : <>{txt.movedTo(p.mine ? txt.yourPost : txt.aPostBy(p.author.name), m.book.title)}{chip}</>}</span>
    </div>
  );
}

/** A Talk position as the board shows it: "62% · p. 133", or "the end" for one sealed until each reader finishes. */
const landing = (position: number, pages: number | null, landmarks?: { name: string; percent: number; page: number }[]) =>
  position >= 100 ? txt.theEnd : posLabel(Math.max(1, position), { pages, myFormat: pages ? "pages" : "percent", landmarks });

function BoardComposer({ ctx }: { ctx: Ctx }) {
  const [body, setBody] = useState("");
  const ta = useRef<HTMLTextAreaElement>(null);
  const post = useMutation({
    mutationFn: () => api.boardPost(ctx.clubId, body),
    onSuccess: async () => { setBody(""); if (ta.current) ta.current.style.height = "auto"; await ctx.refresh(); },
  });
  return (
    <form className="bd-composer" onSubmit={(e) => { e.preventDefault(); if (body.trim()) post.mutate(); }}>
      <Avatar name={ctx.me.name} image={ctx.me.image} />
      <textarea ref={ta} id="bd-compose" rows={1} value={body} maxLength={BOARD_MAX_BODY} placeholder={txt.postSomethingForThe} aria-label={txt.postSomethingForThe2}
        onChange={(e) => { setBody(e.target.value); grow(e.target); }} />
      {body.length > BOARD_MAX_BODY - 150 && <span className="bd-count muted small">{BOARD_MAX_BODY - body.length}</span>}
      <button type="submit" className="btn sm" disabled={!body.trim() || post.isPending}>{txt.post}</button>
      {post.isError && <p className="error small bd-err">{describeError(post.error)}</p>}
    </form>
  );
}

function grow(el: HTMLTextAreaElement) { el.style.height = "auto"; el.style.height = `${Math.min(el.scrollHeight, 12 * 16)}px`; }

function Editor({ ctx, p, onDone }: { ctx: Ctx; p: BoardReply; onDone: () => void }) {
  const [body, setBody] = useState(p.body ?? "");
  const save = useMutation({ mutationFn: () => api.boardEdit(ctx.clubId, p.id, body), onSuccess: async () => { await ctx.refresh(); onDone(); } });
  return (
    <form className="stack-sm bd-editor" onSubmit={(e) => { e.preventDefault(); save.mutate(); }}>
      <textarea id={`bd-edit-${p.id}`} className="input" value={body} maxLength={BOARD_MAX_BODY} onChange={(e) => setBody(e.target.value)} autoFocus />
      <div className="row">
        <button type="submit" className="btn primary sm" disabled={!body.trim() || save.isPending}>{txt.save}</button>
        <button type="button" className="btn ghost sm" onClick={onDone}>{txt.cancel}</button>
      </div>
      {save.isError && <p className="error small">{describeError(save.error)}</p>}
    </form>
  );
}

/**
 * The ··· menu. Posts: reply, pin or unpin (owners and admins), move to the
 * current book (the author, admins), edit (the author), delete (the author,
 * admins). Replies: reply to them, edit, delete.
 */
function PostMenu({ ctx, p, onReply, reply, paper }: { ctx: Ctx; p: BoardReply; onReply?: () => void; reply?: boolean; paper?: boolean }) {
  const [open, setOpen] = useState(false);
  const [panel, setPanel] = useState<null | "pin" | "move" | "delete">(null);
  const ref = useRef<HTMLDivElement>(null);
  const unpin = useMutation({ mutationFn: () => api.boardUnpin(ctx.clubId), onSuccess: ctx.refresh });
  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => { if (!ref.current?.contains(e.target as Node)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);
  const isPinned = ctx.pinned?.post.id === p.id;
  const canMove = !reply && ctx.book && (p.mine || ctx.isAdmin);
  const pick = (f: () => void) => () => { setOpen(false); f(); };
  if (!p.mine && !ctx.isAdmin && !onReply) return null;
  return (
    <div className={`bd-more-wrap${paper ? " paper" : ""}`} ref={ref}>
      <button type="button" className="bd-more" aria-label={txt.more} aria-haspopup="menu" aria-expanded={open} onClick={() => setOpen((o) => !o)}><MoreIcon width={18} height={18} /></button>
      {open && (
        <div className="menu" role="menu">
          {onReply && <button role="menuitem" onClick={pick(onReply)}>{reply ? txt.replyTo(p.mine ? txt.yourself : p.author.name) : txt.reply2}</button>}
          {!reply && ctx.isAdmin && (isPinned
            ? <button role="menuitem" disabled={unpin.isPending} onClick={pick(() => unpin.mutate())}>{txt.unpin}</button>
            : <button role="menuitem" onClick={pick(() => setPanel("pin"))}>{txt.pinToTheTop}</button>)}
          {canMove && <button role="menuitem" className="hl" onClick={pick(() => setPanel("move"))}>{txt.moveTo(ctx.book!.title)}</button>}
          {p.mine && <button role="menuitem" onClick={pick(() => window.dispatchEvent(new CustomEvent("bd-edit", { detail: p.id })))}>{txt.edit}</button>}
          {(p.mine || ctx.isAdmin) && <button role="menuitem" className="danger" onClick={pick(() => setPanel("delete"))}>{txt.delete}</button>}
        </div>
      )}
      {panel === "pin" && <PinPanel ctx={ctx} p={p} onClose={() => setPanel(null)} />}
      {panel === "delete" && <DeletePanel ctx={ctx} p={p} reply={reply} onClose={() => setPanel(null)} />}
      {panel === "move" && ctx.book && <MoveSheet ctx={ctx} p={p} onClose={() => setPanel(null)} />}
      {unpin.isError && <p className="error small">{describeError(unpin.error)}</p>}
    </div>
  );
}

function SmallSheet({ label, onClose, children }: { label: string; onClose: () => void; children: React.ReactNode }) {
  useScrollLock();
  const ref = useRef<HTMLDivElement>(null);
  useSheetDrag(ref, onClose);
  useEffect(() => { const k = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); }; document.addEventListener("keydown", k); return () => document.removeEventListener("keydown", k); }, [onClose]);
  return createPortal(
    <>
      <div className="modal-dim" onClick={onClose} />
      <div className="modal bd-sheet" role="dialog" aria-label={label} ref={ref}>
        <div className="grab" />
        <div className="head"><b>{label}</b><button type="button" aria-label={txt.close} onClick={onClose}><CloseIcon /></button></div>
        <div className="bd-sheet-body">{children}</div>
      </div>
    </>,
    document.body,
  );
}

/** Pin, with an optional date it unpins itself, and a one-line warning naming the notice it replaces. */
function PinPanel({ ctx, p, onClose }: { ctx: Ctx; p: BoardReply; onClose: () => void }) {
  const [until, setUntil] = useState("");
  const pin = useMutation({
    // A date means "until the end of that day", in the viewer's own time.
    mutationFn: () => api.boardPin(ctx.clubId, p.id, until ? new Date(`${until}T23:59:59`).toISOString() : null),
    onSuccess: async () => { await ctx.refresh(); onClose(); },
  });
  const replaces = ctx.pinned && ctx.pinned.post.id !== p.id ? ctx.pinned.post : null;
  const today = new Date(); const min = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
  return (
    <SmallSheet label={txt.pinThisToThe} onClose={onClose}>
      <div className="bd-quote">{excerpt(p.body ?? "", 160)}<div className="muted small">{p.mine ? txt.you2 : p.author.name} · {when(p.createdAt)}</div></div>
      {replaces && <p className="small">{txt.thisReplaces(excerpt(replaces.body ?? ""))}</p>}
      <label className="bd-until small">
        <span>{txt.unpinItOn}<span className="muted">{txt.optional}</span></span>
        <input className="input" type="date" min={min} value={until} onChange={(e) => setUntil(e.target.value)} />
      </label>
      {pin.isError && <p className="error small">{describeError(pin.error)}</p>}
      <div className="row bd-sheet-acts">
        <button type="button" className="btn ghost" onClick={onClose}>{txt.cancel}</button>
        <button type="button" className="btn primary" disabled={pin.isPending} onClick={() => pin.mutate()}>{replaces ? txt.pinThisInstead : txt.pinIt}</button>
      </div>
    </SmallSheet>
  );
}

function DeletePanel({ ctx, p, reply, onClose }: { ctx: Ctx; p: BoardReply; reply?: boolean; onClose: () => void }) {
  const del = useMutation({ mutationFn: () => api.boardDelete(ctx.clubId, p.id), onSuccess: async () => { await ctx.refresh(); onClose(); } });
  const count = !reply && "replyCount" in p ? (p as BoardPost).replyCount : 0;
  return (
    <div className="bd-confirm small" role="alertdialog" aria-label={txt.delete}>
      <span>{txt.deleteThis(reply ? txt.reply : txt.post2, count ? txt.its(plural(count, txt.replyStays, txt.repliesStay)) : "")}</span>
      <button type="button" className="btn danger sm" disabled={del.isPending} onClick={() => del.mutate()}>{txt.delete}</button>
      <button type="button" className="btn ghost sm" onClick={onClose}>{txt.keep}</button>
      {del.isError && <p className="error small">{describeError(del.error)}</p>}
    </div>
  );
}

/**
 * The only confirm for a move, since it can't be undone from the app. It says where the post lands (the
 * author's spot when they posted) and lets the mover seal it further, for a post that gives away more than
 * its author had read: a landmark past that spot, any later page, or the end.
 */
function MoveSheet({ ctx, p, onClose }: { ctx: Ctx; p: BoardReply; onClose: () => void }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const preview = useQuery({ queryKey: ["board-move", ctx.clubId, p.id], queryFn: () => api.boardMovePreview(ctx.clubId, p.id), staleTime: 0 });
  const [picked, setPicked] = useState<number | null>(null); // null = the author's spot
  const [picking, setPicking] = useState(false);
  const [later, setLater] = useState("");
  const v = preview.data;
  const lands = picked ?? v?.position ?? 100;
  const move = useMutation({
    mutationFn: () => api.boardMove(ctx.clubId, p.id, picked),
    onSuccess: async () => {
      await Promise.all([ctx.refresh(), qc.invalidateQueries({ queryKey: ["discussion", ctx.clubId] })]);
      onClose();
      void navigate({ to: ".", search: (s: Record<string, unknown>) => { const { post: _drop, ...rest } = s; return rest; }, replace: true });
    },
  });
  const who = p.mine ? txt.your : txt.s(p.author.name);
  const they = p.mine ? txt.you : txt.they;
  // "Later": typed as a page when the book has pages, else a percent; never below where it already lands.
  const laterPercent = (() => {
    const n = Number(later);
    if (!v || !later.trim() || !Number.isFinite(n) || n <= 0) return null;
    const pct = v.pages ? Math.ceil((n / v.pages) * 100) : Math.round(n);
    return Math.min(100, pct);
  })();
  const laterOk = laterPercent !== null && v !== undefined && laterPercent > v.position;
  const options = v ? v.landmarks.filter((l) => l.percent > v.position && l.percent < 100) : [];
  return (
    <SmallSheet label={txt.moveThisTo(ctx.book!.title)} onClose={onClose}>
      <div className="bd-quote">{p.body}<div className="muted small">{p.mine ? txt.you2 : p.author.name} · {when(p.createdAt)}</div></div>
      {preview.isPending ? <Spinner /> : preview.isError ? <p className="error small">{describeError(preview.error)}</p> : v && (
        <>
          <div className="bd-lands">
            <span className="small muted">{txt.landsAt}</span>
            <button type="button" className={`pos bd-lands-chip${picked !== null ? " raised" : ""}`} aria-expanded={picking} disabled={v.position >= 100}
              onClick={() => setPicking((o) => !o)}>{landing(lands, v.pages, v.landmarks)}{v.position < 100 ? <span aria-hidden="true"> ▾</span> : null}</button>
          </div>
          {picking && v.position < 100 && (
            <div className="bd-pos-pick" role="radiogroup" aria-label={txt.whereItLands}>
              <button type="button" role="radio" aria-checked={picked === null} onClick={() => { setPicked(null); setPicking(false); }}>
                <b>{txt.where(p.mine ? txt.youWere : txt.was(p.author.name))}</b><span>{landing(v.position, v.pages)}</span>
              </button>
              {options.map((l) => (
                <button key={l.id} type="button" role="radio" aria-checked={picked === l.percent} onClick={() => { setPicked(l.percent); setPicking(false); }}>
                  <b>{txt.through(l.name)}</b><span>{v.pages ? txt.p(l.page) : `${l.percent}%`}</span>
                </button>
              ))}
              <div className={`bd-pos-later${laterOk && picked === laterPercent ? " on" : ""}`}>
                <b>{txt.later}</b>
                <input className="input num" inputMode="numeric" type="number" min={1} max={v.pages ?? 100} value={later} aria-label={v.pages ? txt.page : txt.percent}
                  placeholder={v.pages ? txt.page2 : "%"} onChange={(e) => setLater(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter" && laterOk) { e.preventDefault(); setPicked(laterPercent); setPicking(false); } }} />
                <button type="button" className="btn sm" disabled={!laterOk} onClick={() => { setPicked(laterPercent); setPicking(false); }}>{txt.useIt}</button>
              </div>
              {later.trim() && !laterOk && <p className="small muted">{txt.pickSomewherePast(landing(v.position, v.pages))}</p>}
              <button type="button" role="radio" aria-checked={picked === 100} onClick={() => { setPicked(100); setPicking(false); }}>
                <b>{txt.theEnd2}</b><span>{txt.sealedUntilEachReader}</span>
              </button>
            </div>
          )}
          <p className="small muted">
            {!v.known
              ? <>{txt.recordedAnyProgressWhen(p.mine ? txt.youHadnT : txt.hadnT(p.author.name), they)}</>
              : picked === null
                ? <>{txt.thatSSpotWhen(who, they)}</>
                : <>{txt.sealedPastSpotWhen(who, they, landing(v.position, v.pages))}</>}
            {txt.thisCanTBe(v.replies > 0 ? txt.withItEachAt(v.replies === 1 ? txt.theReplyMoves : txt.repliesMove) : "")}</p>
        </>
      )}
      {move.isError && <p className="error small">{describeError(move.error)}</p>}
      <div className="row bd-sheet-acts">
        <button type="button" className="btn ghost" onClick={onClose}>{txt.cancel}</button>
        <button type="button" className="btn primary" disabled={!v || move.isPending} onClick={() => move.mutate()}>{txt.moveIt}</button>
      </div>
    </SmallSheet>
  );
}

// ---------------------------------------------------------------------------
// Threads
// ---------------------------------------------------------------------------

function useThread(clubId: string, postId: string) {
  return useQuery({ queryKey: ["board-thread", clubId, postId], queryFn: () => api.boardThread(clubId, postId), refetchInterval: 20_000 });
}

function Replies({ ctx, replies, names, onReplyTo }: { ctx: Ctx; replies: BoardReply[]; names: string[]; onReplyTo: (name: string) => void }) {
  return (
    <div className="bd-replies">
      {replies.map((r) => (
        <div key={r.id} className="bd-post reply">
          <ProfileFace user={r.author} />
          <div className="bd-body">
            <div className="bd-meta">
              <b>{r.mine ? txt.you2 : r.author.name}</b>
              {r.author.former && <span className="pill">{txt.left}</span>}
              <span className="muted">{when(r.createdAt)}</span>
              <PostMenu ctx={ctx} p={r} reply onReply={() => onReplyTo(r.author.name)} />
            </div>
            <PostBody ctx={ctx} p={r} names={names} className="bd-text" />
          </div>
        </div>
      ))}
    </div>
  );
}

function ReplyBox({ ctx, postId, to, prefill, onPosted }: { ctx: Ctx; postId: string; to: string; prefill: string | null; onPosted?: () => void }) {
  const [body, setBody] = useState("");
  const ta = useRef<HTMLTextAreaElement>(null);
  useEffect(() => { if (prefill) { setBody((b) => (b.startsWith(`@${prefill} `) ? b : `@${prefill} ${b}`)); ta.current?.focus(); } }, [prefill]);
  const send = useMutation({
    mutationFn: () => api.boardReply(ctx.clubId, postId, body),
    onSuccess: async () => { setBody(""); if (ta.current) ta.current.style.height = "auto"; await ctx.refresh(); onPosted?.(); },
  });
  return (
    <form className="bd-replybox" onSubmit={(e) => { e.preventDefault(); if (body.trim()) send.mutate(); }}>
      <Avatar name={ctx.me.name} image={ctx.me.image} />
      <div className="pill-box">
        <textarea ref={ta} id={`bd-reply-${postId}`} rows={1} value={body} maxLength={BOARD_MAX_BODY} placeholder={txt.replyTo2(to)} aria-label={txt.replyTo3(to)}
          onChange={(e) => { setBody(e.target.value); grow(e.target); }}
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey && window.matchMedia("(pointer: fine)").matches) { e.preventDefault(); if (body.trim() && !send.isPending) send.mutate(); } }} />
        <div className="pill-tail"><button type="submit" className="send" disabled={!body.trim() || send.isPending} aria-label={txt.reply2}><SendIcon /></button></div>
      </div>
      {send.isError && <p className="error small bd-err">{describeError(send.error)}</p>}
    </form>
  );
}

/** Desktop: the thread opens under its post. */
function InlineThread({ ctx, postId }: { ctx: Ctx; postId: string }) {
  const t = useThread(ctx.clubId, postId);
  const [prefill, setPrefill] = useState<string | null>(null);
  if (t.isPending) return <Spinner />;
  if (t.isError) return <p className="error small">{describeError(t.error)}</p>;
  const names = [...new Set([t.data.author.name, ...t.data.replies.map((r) => r.author.name)])];
  return (
    <div className="bd-inline">
      <Replies ctx={ctx} replies={t.data.replies} names={names} onReplyTo={(n) => setPrefill(n)} />
      {!t.data.deleted && <ReplyBox ctx={ctx} postId={postId} to={t.data.mine ? "your post" : t.data.author.name} prefill={prefill} />}
    </div>
  );
}

/** Phone: the thread is its own screen, with the reply box at the bottom. */
export function BoardThreadScreen({ club, me, postId, onBack }: { club: Club; me: Me; postId: string; onBack: () => void }) {
  const clubId = club.id;
  const refresh = useRefresh(clubId);
  useMarkSeen(clubId);
  const board = useQuery({ queryKey: ["board", clubId], queryFn: () => api.board(clubId), enabled: false }); // reuse whatever the board has cached
  const t = useThread(clubId, postId);
  const [prefill, setPrefill] = useState<string | null>(null);
  const listRef = useRef<HTMLDivElement>(null);
  useEffect(() => { window.scrollTo(0, 0); }, []);
  const cached = (board.data as unknown as { pages?: { pinned: BoardPin | null; currentBook: Ctx["book"] }[] } | undefined)?.pages?.[0];
  const ctx: Ctx = { clubId, club, me, isAdmin: club.myRole !== "member", book: cached?.currentBook ?? null, pinned: cached?.pinned ?? null, refresh };
  return (
    <main className="page bd-thread-screen">
      <BackBar label={txt.noticeboard} onClick={onBack} />
      {t.isPending ? <Spinner /> : t.isError ? <p className="error">{describeError(t.error)}</p> : (() => {
        const names = [...new Set([t.data.author.name, ...t.data.replies.map((r) => r.author.name)])];
        return (
          <>
            <div className="bd-op">
              <article className="bd-post">
                {t.data.deleted ? <Avatar name={t.data.author.name} image={t.data.author.image} /> : <ProfileFace user={t.data.author} />}
                <div className="bd-body">
                  <div className="bd-meta"><b>{t.data.deleted ? "" : t.data.mine ? txt.you2 : t.data.author.name}</b><span className="muted">{when(t.data.createdAt)}</span>{!t.data.deleted && <PostMenu ctx={ctx} p={t.data} />}</div>
                  <PostBody ctx={ctx} p={t.data} names={names} className="bd-text" />
                </div>
              </article>
            </div>
            <div ref={listRef}>
              {t.data.replies.length === 0 ? <p className="muted small">{txt.noRepliesYet}</p> : <Replies ctx={ctx} replies={t.data.replies} names={names} onReplyTo={(n) => setPrefill(n)} />}
            </div>
            {!t.data.deleted && (
              <div className="bd-thread-foot">
                <ReplyBox ctx={ctx} postId={postId} to={t.data.mine ? "your post" : t.data.author.name} prefill={prefill}
                  onPosted={() => requestAnimationFrame(() => window.scrollTo({ top: document.body.scrollHeight, behavior: "smooth" }))} />
              </div>
            )}
          </>
        );
      })()}
    </main>
  );
}

/** The miniature paper-and-pin mark, for Around the club. */
export function MiniNotice() { return <span className="mini-notice" aria-hidden="true" />; }
